'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import { Button } from './Button';
import styles from './JobCardSnow.module.css';

export interface JobCardSnowAttribute {
  id: number | string;
  label: string;
  emoji?: string;
}

export interface JobCardSnowProps extends React.HTMLAttributes<HTMLDivElement> {
  job: {
    id: number | string;
    title: string;
    company: string;
    companyLogo?: string;
    description?: string;
    attributes?: JobCardSnowAttribute[];
  };
  ctaLabel?: string;
  recruiterLogoUrl?: string;
  recruiterName?: string;
  className?: string;
}

const ArrowIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3 8h9M8.5 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const JobCardSnow = React.forwardRef<HTMLDivElement, JobCardSnowProps>(
  (
    {
      job,
      ctaLabel = 'View job',
      recruiterLogoUrl,
      recruiterName,
      className,
      ...props
    },
    ref
  ) => {
    const attributes = job.attributes ?? [];

    return (
      <div
        ref={ref}
        id={`card-${job.id}`}
        className={clsx(styles.root, className)}
        {...props}
      >
        <div className={styles.card}>
          <div className={styles.top}>
            <div className={styles.headings}>
              <h2 className={styles.name}>{job.title}</h2>
              <p className={styles.title}>{job.company}</p>
              {job.description && (
                <p className={styles.highlights}>{job.description}</p>
              )}
            </div>

            {job.companyLogo && (
              <div className={styles.recruiterLogo}>
                {recruiterLogoUrl ? (
                  <img src={recruiterLogoUrl} alt={recruiterName || ''} />
                ) : (
                  <div className={styles.recruiterLogoPlaceholder} aria-hidden="true">
                    Logo
                  </div>
                )}
              </div>
            )}
          </div>

          <Button
            variant="primary"
            size="lg"
            className={styles.cta}
            endIcon={
              <span className={styles.ctaIcon}>
                <ArrowIcon />
              </span>
            }
            tabIndex={-1}
            aria-hidden="true"
          >
            {ctaLabel}
          </Button>

          <div className={styles.bottom}>
            <div className={styles.avatarPanel}>
              <div className={styles.avatarWrapper}>
                {job.companyLogo ? (
                  <img
                    src={job.companyLogo}
                    alt={job.company}
                    className={styles.avatarImage}
                  />
                ) : recruiterLogoUrl ? (
                  <img
                    src={recruiterLogoUrl}
                    alt={recruiterName || job.company}
                    className={styles.avatarImage}
                  />
                ) : (
                  <div className={styles.avatarFallback}>
                    {job.company
                      .split(' ')
                      .map((n) => n[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase()}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.skillsPanel}>
              <p className={styles.skillsLabel}>Details:</p>
              <div className={styles.skillsList}>
                {attributes.map((item) => (
                  <span className={styles.skill} key={item.id}>
                    {item.emoji && <span className={styles.skillEmoji}>{item.emoji}</span>}
                    {item.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

JobCardSnow.displayName = 'JobCardSnow';

export default JobCardSnow;
